import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { ROUTES } from 'config/navConfig';

const SECTORS = [
  'Renewable Energy', 'Transport Infrastructure', 'Digital Infrastructure',
  'Water & Utilities', 'Social Infrastructure', 'Energy Transition', 'Other',
];
const GEOGRAPHIES = ['United Kingdom', 'Western Europe', 'Nordics', 'Southern Europe', 'Africa', 'Middle East', 'Asia Pacific', 'Other'];
const STAGES = ['Early Development', 'Advanced Development', 'Construction', 'Operational', 'Refinancing', 'Secondary Sale'];

const STEPS = [
  { id: 'basics', label: 'Deal Basics', icon: 'FileText' },
  { id: 'financials', label: 'Financials', icon: 'PoundSterling' },
  { id: 'documents', label: 'Documents', icon: 'FolderLock' },
  { id: 'review', label: 'Review & Submit', icon: 'Send' },
];

const DEMO_FORM = {
  dealName: 'Aberdeen Pumped Hydro Storage',
  sector: 'Renewable Energy',
  geography: 'United Kingdom',
  stage: 'Advanced Development',
  dealSize: '£320m',
  targetIRR: '11.5',
  equityTicket: '£85m',
  description: 'Pumped hydro storage facility in Aberdeenshire providing 1.4GW of flexible grid balancing capacity. 25-year capacity market agreement with National Grid ESO. Construction-ready — planning consent granted February 2026. Caledonian Energy Partners has developed four comparable assets across Scotland since 2018.',
  highlights: 'Planning consent secured · 25-year capacity contract with National Grid ESO · Construction ready Q3 2026 · Operational precedent — 4 comparable assets',
  sponsorName: 'Caledonian Energy Partners Ltd',
  sponsorTrackRecord: 'Founded 2011. £1.4bn AUM. Four operational pumped hydro assets in Scotland. Greenfield and brownfield track record. FCA authorised.',
  contactName: 'Emma Richardson',
  contactEmail: 'e.richardson@caledonianenergy.co.uk',
  nda: false,
};

export default function DealSubmission() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState(DEMO_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const validateStep = (stepIdx) => {
    const errs = {};
    if (stepIdx === 0) {
      if (!form.dealName.trim()) errs.dealName = 'Required';
      if (!form.sector) errs.sector = 'Required';
      if (!form.geography) errs.geography = 'Required';
      if (!form.stage) errs.stage = 'Required';
      if (!form.description.trim()) errs.description = 'Required';
    }
    if (stepIdx === 1) {
      if (!form.dealSize.trim()) errs.dealSize = 'Required';
      if (!form.targetIRR.trim()) errs.targetIRR = 'Required';
    }
    if (stepIdx === 3) {
      if (!form.contactName.trim()) errs.contactName = 'Required';
      if (!form.contactEmail.trim()) errs.contactEmail = 'Required';
      if (!form.nda) errs.nda = 'You must confirm NDA terms to submit.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setErrors({});
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = () => {
    if (!validateStep(3)) return;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
          <div className="card p-10 max-w-lg w-full text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(5,150,105,0.12)' }}>
              <Icon name="CheckCircle" size={32} color="#059669" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">Submission Received</h2>
            <p className="font-caption text-sm text-muted-foreground mb-1">
              Thank you for submitting <strong className="text-foreground">{form.dealName}</strong> to SeedDrive.
            </p>
            <p className="font-caption text-sm text-muted-foreground mb-6">
              Our investment team will review your submission and contact you at <strong className="text-foreground">{form.contactEmail}</strong> within 5 business days.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="default" iconName="TrendingUp" iconPosition="left" iconSize={15} onClick={() => navigate(ROUTES.opportunities)}>
                Browse Opportunities
              </Button>
              <Button variant="outline" iconName="Plus" iconPosition="left" iconSize={15} onClick={() => { setSubmitted(false); setForm(EMPTY_FORM); setCurrentStep(0); }}>
                Submit Another Deal
              </Button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4 md:py-5" style={{ zIndex: 'var(--z-nav)' }}>
          <nav className="flex items-center gap-2 mb-4 text-sm font-caption text-muted-foreground">
            <button onClick={() => navigate(ROUTES.opportunities)} className="hover:text-primary transition-colors">Marketplace</button>
            <Icon name="ChevronRight" size={14} />
            <span className="text-foreground font-medium">Submit a Deal</span>
          </nav>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-1">Submit a Deal</h1>
              <p className="font-caption text-sm text-muted-foreground">
                Share your infrastructure deal with SeedDrive&apos;s investment team for consideration.
              </p>
            </div>
            <Button variant="outline" iconName="ArrowLeft" iconPosition="left" iconSize={16} onClick={() => navigate(ROUTES.opportunities)}>
              Back
            </Button>
          </div>
        </div>

        <div className="px-6 md:px-10 lg:px-12 py-8">
          {/* Step progress */}
          <div className="flex items-center gap-0 mb-8 overflow-x-auto">
            {STEPS.map((step, idx) => {
              const isDone = idx < currentStep;
              const isCurrent = idx === currentStep;
              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center"
                      style={{
                        background: isCurrent ? 'rgba(59,76,76,0.12)' : isDone ? 'rgba(5,150,105,0.12)' : 'var(--color-muted)',
                        border: `2px solid ${isCurrent ? 'var(--color-primary)' : isDone ? '#059669' : 'var(--color-border)'}`,
                      }}
                    >
                      {isDone
                        ? <Icon name="Check" size={14} color="#059669" strokeWidth={3} />
                        : <Icon name={step.icon} size={15} color={isCurrent ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                      }
                    </div>
                    <span className="font-caption text-xs mt-1.5 whitespace-nowrap" style={{ color: isCurrent ? 'var(--color-primary)' : isDone ? '#059669' : 'var(--color-muted-foreground)', fontWeight: isCurrent ? 600 : 400 }}>
                      {step.label}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className="flex-1 h-0.5 mb-5 mx-2" style={{ background: idx < currentStep ? '#059669' : 'var(--color-border)', minWidth: 24 }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="card p-6 md:p-8">
            {/* Step 0 — Deal Basics */}
            {currentStep === 0 && (
              <div className="space-y-5">
                <h2 className="font-heading font-semibold text-foreground text-lg mb-1">Deal Basics</h2>
                <p className="font-caption text-sm text-muted-foreground mb-4">Tell us about the infrastructure opportunity.</p>

                <Field label="Deal Name" required error={errors.dealName}>
                  <input className="form-input" value={form.dealName} onChange={(e) => set('dealName', e.target.value)} placeholder="e.g. Northumbria Offshore Wind Phase II" />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Sector" required error={errors.sector}>
                    <select className="form-input" value={form.sector} onChange={(e) => set('sector', e.target.value)}>
                      <option value="">Select sector</option>
                      {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Geography" required error={errors.geography}>
                    <select className="form-input" value={form.geography} onChange={(e) => set('geography', e.target.value)}>
                      <option value="">Select geography</option>
                      {GEOGRAPHIES.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </Field>
                  <Field label="Development Stage" required error={errors.stage}>
                    <select className="form-input" value={form.stage} onChange={(e) => set('stage', e.target.value)}>
                      <option value="">Select stage</option>
                      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Deal Description" required error={errors.description}>
                  <textarea className="form-input" rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Describe the asset, location, technology, and key investment thesis..." />
                </Field>
                <Field label="Key Highlights (optional)">
                  <textarea className="form-input" rows={3} value={form.highlights} onChange={(e) => set('highlights', e.target.value)} placeholder="e.g. 15-year CfD, operational Phase I, fixed-price EPC..." />
                </Field>
              </div>
            )}

            {/* Step 1 — Financials */}
            {currentStep === 1 && (
              <div className="space-y-5">
                <h2 className="font-heading font-semibold text-foreground text-lg mb-1">Financials</h2>
                <p className="font-caption text-sm text-muted-foreground mb-4">Indicative financial parameters for the opportunity.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Total Deal Size" required error={errors.dealSize}>
                    <input className="form-input" value={form.dealSize} onChange={(e) => set('dealSize', e.target.value)} placeholder="e.g. £120m" />
                  </Field>
                  <Field label="Target Equity IRR" required error={errors.targetIRR}>
                    <input className="form-input" value={form.targetIRR} onChange={(e) => set('targetIRR', e.target.value)} placeholder="e.g. 12–15%" />
                  </Field>
                  <Field label="Minimum Equity Ticket (optional)">
                    <input className="form-input" value={form.equityTicket} onChange={(e) => set('equityTicket', e.target.value)} placeholder="e.g. £5m" />
                  </Field>
                </div>
              </div>
            )}

            {/* Step 2 — Documents */}
            {currentStep === 2 && (
              <div className="space-y-5">
                <h2 className="font-heading font-semibold text-foreground text-lg mb-1">Documents</h2>
                <p className="font-caption text-sm text-muted-foreground mb-4">Supporting materials help our team assess the opportunity quickly.</p>

                <div
                  className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 text-center"
                  style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)' }}
                >
                  <Icon name="Upload" size={28} color="var(--color-muted-foreground)" />
                  <p className="font-caption font-medium text-foreground mt-3 mb-1">Upload deal documents</p>
                  <p className="font-caption text-xs text-muted-foreground">Investment teaser, financial model, technical reports (PDF, XLSX, PPTX — max 25MB each)</p>
                  <p className="font-caption text-xs text-muted-foreground mt-3 italic">Document upload is available in the full platform. You can proceed without uploading now.</p>
                </div>
              </div>
            )}

            {/* Step 3 — Review & Submit */}
            {currentStep === 3 && (
              <div className="space-y-5">
                <h2 className="font-heading font-semibold text-foreground text-lg mb-1">Review &amp; Submit</h2>
                <p className="font-caption text-sm text-muted-foreground mb-4">Confirm your contact details and review your submission.</p>

                {/* Summary */}
                <div className="rounded-xl p-4 space-y-2" style={{ background: 'rgba(59,76,76,0.05)', border: '1px solid rgba(59,76,76,0.12)' }}>
                  <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Submission Summary</p>
                  {[
                    { label: 'Deal Name', value: form.dealName || '—' },
                    { label: 'Sector', value: form.sector || '—' },
                    { label: 'Geography', value: form.geography || '—' },
                    { label: 'Stage', value: form.stage || '—' },
                    { label: 'Deal Size', value: form.dealSize || '—' },
                    { label: 'Target IRR', value: form.targetIRR || '—' },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between">
                      <span className="font-caption text-xs text-muted-foreground">{label}</span>
                      <span className="font-caption text-xs font-medium text-foreground">{value}</span>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Your Name" required error={errors.contactName}>
                    <input className="form-input" value={form.contactName} onChange={(e) => set('contactName', e.target.value)} placeholder="Full name" />
                  </Field>
                  <Field label="Your Email" required error={errors.contactEmail}>
                    <input className="form-input" type="email" value={form.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} placeholder="your@email.com" />
                  </Field>
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <input
                    id="nda"
                    type="checkbox"
                    checked={form.nda}
                    onChange={(e) => set('nda', e.target.checked)}
                    className="mt-0.5 flex-shrink-0"
                  />
                  <label htmlFor="nda" className="font-caption text-sm text-foreground cursor-pointer">
                    I confirm that submitting this deal does not breach any confidentiality obligations and I accept SeedDrive&apos;s standard NDA terms for deal origination submissions.
                  </label>
                </div>
                {errors.nda && <p className="font-caption text-xs text-red-600">{errors.nda}</p>}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-6 mt-6 border-t border-border">
              <Button variant="outline" iconName="ArrowLeft" iconPosition="left" iconSize={15} onClick={currentStep === 0 ? () => navigate(ROUTES.opportunities) : handleBack}>
                {currentStep === 0 ? 'Cancel' : 'Back'}
              </Button>
              {currentStep < STEPS.length - 1 ? (
                <Button variant="default" iconName="ArrowRight" iconPosition="right" iconSize={15} onClick={handleNext}>
                  Continue
                </Button>
              ) : (
                <Button variant="default" iconName="Send" iconPosition="left" iconSize={15} onClick={handleSubmit}>
                  Submit Deal
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="block font-caption text-sm font-medium text-foreground mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="font-caption text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}
